import { FormEvent, useEffect, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import { GENDERS } from 'components/intake/PatientInfoSection';
import Button from 'components/shared/Button';
import FileInputComponent from 'components/shared/fileInput/FileInputComponent';
import DatePicker from 'components/shared/form/DatePicker';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import { SKILLED_NURSING_FACILITY } from 'constants/locationTypes';
import Upload from 'hooks/useDirectUpload/Upload';
import { parseDate, simpleDashDate } from 'lib/date';
import Workflow, { WorkflowAction } from 'models/Workflow';
import { GroupAccessMode, groupsQueryKeys, indexGroups } from 'services/api/group';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import {
  CollectedName,
  collectedNameValue,
  PORTAL_COLLECT_FIELDS,
  ReferralFieldDef,
  sourceSystemLabel,
  VALUE_NAMES,
} from './constants';

interface Props {
  action: WorkflowAction;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => void;
  isCompleting: boolean;
  // Values read off the portal elsewhere (the extension's manual HUD) — they land in
  // the entry fields for the person to review, never overwriting what they've typed.
  // Strings prefill the text fields; the facilities list arrives as a string array.
  prefill?: Record<string, unknown> | null;
}

// The fields whose entry state is one plain string. 'date' and 'name' own their own
// state (a Date, and a shape the person chooses), so they are not in the text record.
const TEXT_COLLECT_FIELDS = PORTAL_COLLECT_FIELDS.filter(
  (field) => field.type === 'text' || field.type === 'select'
);

interface FacilityOption {
  id: string;
  name: string;
}

// A facility row is either a direct pick of an Olio facility (a dropdown — the pick
// skips the mapping step entirely) or a portal-named string (HUD prefill; the mapping
// step resolves it). Rows are positional; no identity beyond their slot.
type FacilityRow = { kind: 'pick'; pick: FacilityOption | null } | { kind: 'text'; text: string };

// Where to go get the information — the whole instruction the kickoff carried. A URL
// pointer renders as a link (and later becomes the browser-extension hand-off).
function SourceContext({ workflow }: { workflow: Workflow }) {
  const system = workflow.valueFor('source_system')?.value;
  const pointer = workflow.valueFor('retrieval_pointer')?.value;
  if (!system && !pointer) return null;

  return (
    <SourceLine data-testid='retrieve-source-context'>
      {system && <BodySmallBold>{sourceSystemLabel(system)}</BodySmallBold>}
      {pointer &&
        (/^https?:\/\//.test(pointer) ? (
          <PointerLink href={pointer} target='_blank' rel='noreferrer'>
            {pointer}
          </PointerLink>
        ) : (
          <Pointer>{pointer}</Pointer>
        ))}
    </SourceLine>
  );
}

// Document-first manual fallback for the source-portal pull: the person saves the
// referral documents out of the portal and uploads them here — the AI actions read the
// demographics from the documents. The collect-fields are the optional shortcut: any
// value typed here is committed at completion and outranks the downstream extraction,
// so what a person read off the portal is never overwritten by the AI.
export default function RetrievePortalForm({ action, workflow, onComplete, isCompleting, prefill }: Props) {
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [text, setText] = useState<Record<string, string>>(() =>
    Object.fromEntries(TEXT_COLLECT_FIELDS.map((field) => [field.name, '']))
  );
  const [dob, setDob] = useState<Date | null>(null);
  // The collected name, in whichever shape the portal put in front of the person: the
  // first/last pair by default, or one name field when that is all the portal shows.
  const [nameParts, setNameParts] = useState({ first: '', last: '' });
  const [fullName, setFullName] = useState('');
  const [singleName, setSingleName] = useState(false);
  // The receiving facilities — n-ary, one row each: direct picks by default (they skip
  // the mapping step), portal-named strings when the HUD read them off the portal.
  const [facilities, setFacilities] = useState<FacilityRow[]>([{ kind: 'pick', pick: null }]);

  // The same facility source the referral modal picks from.
  const facilityOptionsQuery = useQuery({
    queryKey: [...groupsQueryKeys.index, 'portal-retrieval-facilities'],
    queryFn: ({ signal }) =>
      indexGroups({ accessModes: [GroupAccessMode.Allowed], type: SKILLED_NURSING_FACILITY, pageSize: 200, sortBy: 'name' }, signal),
  });
  const facilityOptions: FacilityOption[] = (facilityOptionsQuery.data?.data ?? []).map((group) => ({
    id: group.id,
    name: group.name,
  }));

  const rowFilled = (row: FacilityRow) => (row.kind === 'pick' ? !!row.pick : !!row.text.trim());

  // Prefill fills only fields the person hasn't touched — their own typing wins.
  useEffect(() => {
    if (!prefill) return;
    const stringAt = (name: string) => {
      const value = prefill[name];
      return typeof value === 'string' ? value : '';
    };
    setText((prev) =>
      Object.fromEntries(Object.entries(prev).map(([name, value]) => [name, value || stringAt(name)]))
    );
    if (stringAt(VALUE_NAMES.PATIENT_DOB)) {
      setDob((prev) => prev ?? parseDate(stringAt(VALUE_NAMES.PATIENT_DOB)) ?? null);
    }
    const prefillName = prefill[VALUE_NAMES.PATIENT_NAME];
    if (prefillName && typeof prefillName === 'object') {
      const collected = prefillName as CollectedName;
      if (collected.full) {
        setSingleName(true);
        setFullName((prev) => prev || collected.full || '');
      } else {
        setNameParts((prev) =>
          prev.first || prev.last ? prev : { first: collected.first ?? '', last: collected.last ?? '' }
        );
      }
    }
    const prefillFacilities = prefill[VALUE_NAMES.FACILITIES];
    if (Array.isArray(prefillFacilities) && prefillFacilities.length > 0) {
      setFacilities((prev) =>
        prev.some(rowFilled) ? prev : prefillFacilities.map((name) => ({ kind: 'text', text: String(name) }))
      );
    }
  }, [prefill]);

  const readyUploads = uploads.filter((upload) => upload.signedId);
  const ready = readyUploads.length > 0 && !isUploading && !isCompleting;

  const typedValues = (): WorkflowValuesInput => {
    const values: WorkflowValuesInput = {};
    TEXT_COLLECT_FIELDS.forEach((field) => {
      const value = text[field.name].trim();
      if (value) values[field.name] = value;
    });
    const dashDob = simpleDashDate(dob);
    if (dashDob) values[VALUE_NAMES.PATIENT_DOB] = dashDob;
    const collectedName = collectedNameValue(singleName ? { full: fullName } : nameParts);
    if (collectedName) values[VALUE_NAMES.PATIENT_NAME] = collectedName;
    return values;
  };

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;
    onComplete(action.id, {
      attachments: readyUploads.map((upload) => ({ signedId: upload.signedId!, filename: upload.file.name })),
      // Always sent, even empty — the mapping step gates on the value's presence and
      // asks a person when there's nothing (or nothing recognized) to map. Direct picks
      // travel as {name, direct: true}; portal-named strings travel verbatim.
      [VALUE_NAMES.FACILITIES]: facilities.flatMap((row): (string | { name: string; direct: boolean })[] => {
        if (row.kind === 'pick') return row.pick ? [{ name: row.pick.name, direct: true }] : [];
        const trimmed = row.text.trim();
        return trimmed ? [trimmed] : [];
      }),
      ...typedValues(),
    });
  }

  const setFacilityRow = (index: number, row: FacilityRow) =>
    setFacilities((prev) => prev.map((current, i) => (i === index ? row : current)));

  const removeFacility = (index: number) =>
    setFacilities((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : [{ kind: 'pick', pick: null }]
    );

  const renderField = (field: ReferralFieldDef) => (
    <Field key={field.name}>
      <FieldLabel>
        {field.label}
        <Optional> (optional)</Optional>
      </FieldLabel>
      {field.type === 'date' && <DatePicker selected={dob} onChange={setDob} $fullWidth placeholder='MM/DD/YYYY' />}
      {field.type === 'select' && (
        <div data-testid={`retrieve-field-${field.name}`}>
          <Select
            inputId={`retrieve-field-${field.name}`}
            placeholder='Select'
            options={GENDERS}
            value={GENDERS.find((option) => option.value === text[field.name]) ?? null}
            onChange={(option) => setText((prev) => ({ ...prev, [field.name]: option?.value ?? '' }))}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
          />
        </div>
      )}
      {field.type === 'name' && (
        <div data-testid={`retrieve-field-${field.name}`}>
          {singleName ? (
            <Input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder='Name as the portal shows it'
              data-testid={`retrieve-field-${field.name}-full`}
            />
          ) : (
            <NameRow>
              <Input
                value={nameParts.first}
                onChange={(e) => setNameParts((prev) => ({ ...prev, first: e.target.value }))}
                placeholder='First'
                data-testid={`retrieve-field-${field.name}-first`}
              />
              <Input
                value={nameParts.last}
                onChange={(e) => setNameParts((prev) => ({ ...prev, last: e.target.value }))}
                placeholder='Last'
                data-testid={`retrieve-field-${field.name}-last`}
              />
            </NameRow>
          )}
          <AddLink type='button' onClick={() => setSingleName((prev) => !prev)}>
            {singleName ? 'Enter first and last separately' : 'The portal shows one name field'}
          </AddLink>
        </div>
      )}
      {field.type === 'text' && (
        <Input
          value={text[field.name]}
          onChange={(e) => setText((prev) => ({ ...prev, [field.name]: e.target.value }))}
          data-testid={`retrieve-field-${field.name}`}
        />
      )}
    </Field>
  );

  return (
    <Form onSubmit={submit} data-testid='retrieve-portal-form'>
      <SourceContext workflow={workflow} />
      <BodySmall>
        Save the referral documents from the source portal (download or print to PDF), then upload them here. The
        patient&apos;s details are read from the documents.
      </BodySmall>
      <UploadZone data-testid='retrieve-documents'>
        <FileInputComponent onChange={setUploads} onUploading={setIsUploading} renderUploadsInternally />
      </UploadZone>
      <Field data-testid='retrieve-facilities'>
        <FieldLabel>Facilities</FieldLabel>
        {facilities.map((row, index) => (
          // Index keys are safe here: rows are positional (no identity beyond their slot).
          <FacilityRowLine key={index}>
            {row.kind === 'pick' ? (
              <SelectCell data-testid={`retrieve-facility-${index}`}>
                <Select<FacilityOption>
                  inputId={`retrieve-facility-${index}`}
                  placeholder='Select a facility'
                  options={facilityOptions}
                  value={row.pick}
                  onChange={(pick) => setFacilityRow(index, { kind: 'pick', pick })}
                  menuPortalTarget={document.body}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                />
              </SelectCell>
            ) : (
              <Input
                value={row.text}
                onChange={(e) => setFacilityRow(index, { kind: 'text', text: e.target.value })}
                data-testid={`retrieve-facility-${index}`}
              />
            )}
            <RowButton
              type='button'
              aria-label='Remove facility'
              onClick={() => removeFacility(index)}
              data-testid={`retrieve-facility-remove-${index}`}>
              ×
            </RowButton>
          </FacilityRowLine>
        ))}
        <AddLink
          type='button'
          onClick={() => setFacilities((prev) => [...prev, { kind: 'pick', pick: null }])}
          data-testid='retrieve-facility-add'>
          + Add facility
        </AddLink>
      </Field>
      <BodySmall>Anything you already know from the portal can be entered directly:</BodySmall>
      <FieldGrid data-testid='retrieve-collect-fields'>{PORTAL_COLLECT_FIELDS.map(renderField)}</FieldGrid>
      <div>
        <Button type='submit' size='small' disabled={!ready} data-testid='retrieve-submit'>
          Save retrieved documents
        </Button>
      </div>
    </Form>
  );
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UploadZone = styled.div`
  margin-top: 4px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px 12px;
`;

const NameRow = styled.div`
  display: flex;
  gap: 8px;

  > * {
    flex: 1;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Optional = styled.span`
  color: ${colors.black50};
  font-weight: 400;
`;

const FacilityRowLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 320px;
`;

const SelectCell = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowButton = styled.button`
  background: none;
  border: none;
  padding: 0 4px;
  cursor: pointer;
  color: ${colors.black50};
  font-size: 16px;
  line-height: 1;
`;

const AddLink = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  font-size: 12px;
  text-decoration: underline;
`;

const SourceLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid ${colors.black10};
  border-radius: 6px;
  background: ${colors.black5};
`;

const Pointer = styled(BodySmall)`
  color: ${colors.black50};
  word-break: break-all;
`;

const PointerLink = styled.a`
  font-size: 12px;
  color: ${colors.blue100};
  word-break: break-all;
`;
